import axios from 'axios';
import { getToken, setSession, clearSession } from './auth';

const BASE = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:3000';
const api  = axios.create({ baseURL: `${BASE}/api` });

api.interceptors.request.use((config) => {
  const token = getToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
    config._sotToken = token; // lets the 401 handler tell which token the request used
  }
  return config;
});

// A 401 on a request that carried the current token means the session is over.
// Login 401s are bad credentials, and a change-password 401 is a wrong current
// password unless it came from the auth middleware (error "Unauthorized").
api.interceptors.response.use((res) => res, (error) => {
  const { response, config } = error;
  if (response && response.status === 401 && config && config._sotToken) {
    const url = config.url || '';
    const isLogin  = url === '/auth/login';
    const isChange = url === '/auth/change-password';
    const sessionEnded = !isLogin && (!isChange || (response.data && response.data.error === 'Unauthorized'));
    // Ignore stale requests: after a password change the stored token is a newer one.
    if (sessionEnded && getToken() === config._sotToken) {
      clearSession();
      window.dispatchEvent(new CustomEvent('sot:signed-out', { detail: { reason: 'expired' } }));
    }
  }
  return Promise.reject(error);
});

export const login = (email, password) =>
  api.post('/auth/login', { email, password }).then(r => { setSession(r.data); return r.data; });
export const getMe = () => api.get('/auth/me').then(r => r.data.user);
// The server revokes every older token on a password change, so keep the fresh one it returns.
export const changePassword = (currentPassword, newPassword) =>
  api.post('/auth/change-password', { currentPassword, newPassword })
    .then(r => { setSession({ token: r.data.token }); return r.data; });

export const getRecords        = (params) => api.get('/records', { params }).then(r => r.data);
export const getRecord         = (id)     => api.get(`/records/${id}`).then(r => r.data);
export const getMetrics        = (params) => api.get('/records/metrics', { params }).then(r => r.data);
export const getSegments       = ()       => api.get('/records/segments').then(r => r.data);
export const createRecord      = (data)   => api.post('/records', data).then(r => r.data);
export const updateRecord      = (id, data) => api.put(`/records/${id}`, data).then(r => r.data);
export const deleteRecord      = (id)     => api.delete(`/records/${id}`).then(r => r.data);
export const bulkDeleteRecords = (ids)    => api.post('/records/bulk-delete', { ids }).then(r => r.data);
export const getAuditLog       = (recordId) => api.get(`/audit/${recordId}`).then(r => r.data);

export const previewImport = (formData) =>
  api.post('/import/preview', formData, { headers: { 'Content-Type': 'multipart/form-data' }}).then(r => r.data);
export const executeImport = (formData) =>
  api.post('/import/execute', formData, { headers: { 'Content-Type': 'multipart/form-data' }}).then(r => r.data);

export const previewFirmImport = (formData) =>
  api.post('/import-firms/preview', formData, { headers: { 'Content-Type': 'multipart/form-data' }}).then(r => r.data);
export const executeFirmImport = (formData) =>
  api.post('/import-firms/execute', formData, { headers: { 'Content-Type': 'multipart/form-data' }}).then(r => r.data);

export const getCtoCandidates   = ()       => api.get('/cto-tracker').then(r => r.data);
export const createCtoCandidate = (data)   => api.post('/cto-tracker', data).then(r => r.data);
export const updateCtoCandidate = (id, data) => api.put(`/cto-tracker/${id}`, data).then(r => r.data);
export const deleteCtoCandidate = (id)     => api.delete(`/cto-tracker/${id}`).then(r => r.data);
export const getCtoAudit        = (id)     => api.get(`/cto-tracker/${id}/audit`).then(r => r.data);

export const getFirms   = ()       => api.get('/firm-pipeline').then(r => r.data);
export const getFirm    = (id)     => api.get(`/firm-pipeline/${id}`).then(r => r.data);
export const createFirm = (data)   => api.post('/firm-pipeline', data).then(r => r.data);
export const updateFirm = (id, data) => api.put(`/firm-pipeline/${id}`, data).then(r => r.data);
export const deleteFirm = (id)     => api.delete(`/firm-pipeline/${id}`).then(r => r.data);

export const convertFirmToDesignPartner = (firmId) =>
  api.post(`/firm-pipeline/${firmId}/convert-to-design-partner`).then(r => r.data);

export const addFirmCall    = (firmId, data)         => api.post(`/firm-pipeline/${firmId}/calls`, data).then(r => r.data);
export const updateFirmCall = (firmId, callId, data) => api.put(`/firm-pipeline/${firmId}/calls/${callId}`, data).then(r => r.data);
export const deleteFirmCall = (firmId, callId)       => api.delete(`/firm-pipeline/${firmId}/calls/${callId}`).then(r => r.data);

export const addFirmTask    = (firmId, data)         => api.post(`/firm-pipeline/${firmId}/tasks`, data).then(r => r.data);
export const updateFirmTask = (firmId, taskId, data) => api.put(`/firm-pipeline/${firmId}/tasks/${taskId}`, data).then(r => r.data);
export const deleteFirmTask = (firmId, taskId)       => api.delete(`/firm-pipeline/${firmId}/tasks/${taskId}`).then(r => r.data);